"use client";

import { Card, CardContent } from "@/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { useMemo } from "react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { useGradingTable } from "./useGrade";

const chartConfig = {
  grade: {
    label: "Cijfer",
    color: "var(--chart-2)",
  },
  passingGrade: {
    label: "Voldoende",
    color: "var(--chart-1)",
  },
} satisfies ChartConfig;

interface GradingChartProps {
  showCard?: boolean;
}

export default function GradingChart({ showCard = true }: GradingChartProps) {
  const { maxScore, possibleScores, passingGrade } = useGradingTable();

  const passingGradesLine = useMemo(() => {
    return possibleScores.map((score) => ({
      ...score,
      passingGrade: Number(passingGrade),
    }));
  }, [possibleScores, passingGrade]);

  const chartContent = (
    <ChartContainer config={chartConfig}>
      <AreaChart
        accessibilityLayer
        data={passingGradesLine}
        margin={{
          left: 12,
          right: 12,
        }}
      >
        <XAxis
          dataKey="score"
          domain={[0, "datamax"]}
          allowDataOverflow={true}
          tickCount={Number(maxScore)}
        />
        <YAxis
          dataKey="grade"
          type="number"
          domain={[1, 10]}
          width={24}
          allowDataOverflow={true}
        />
        <CartesianGrid />
        <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
        <defs>
          <linearGradient id="fillPassingGrade" x1="0" y1="0" x2="0" y2="1">
            <stop
              offset="5%"
              stopColor="var(--color-passingGrade)"
              stopOpacity={0.8}
            />
            <stop
              offset="95%"
              stopColor="var(--color-passingGrade)"
              stopOpacity={0.1}
            />
          </linearGradient>
          <linearGradient id="fillGrade" x1="0" y1="0" x2="0" y2="1">
            <stop
              offset="5%"
              stopColor="var(--color-grade)"
              stopOpacity={0.8}
            />
            <stop
              offset="95%"
              stopColor="var(--color-grade)"
              stopOpacity={0.1}
            />
          </linearGradient>
        </defs>
        <Area
          dataKey="passingGrade"
          type="natural"
          stroke={chartConfig.passingGrade.color}
          fill="url(#fillPassingGrade)"
          strokeWidth={2}
          dot={false}
          fillOpacity={0.4}
          isAnimationActive={false}
        />
        <Area
          dataKey="grade"
          type="natural"
          stroke={chartConfig.grade.color}
          fill="url(#fillGrade)"
          strokeWidth={2}
          dot={false}
          fillOpacity={0.4}
          isAnimationActive={false}
        />
      </AreaChart>
    </ChartContainer>
  );

  if (!showCard) {
    return chartContent;
  }

  return (
    <Card>
      <CardContent className="pt-6">{chartContent}</CardContent>
    </Card>
  );
}
